import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApplicationCoordinationStatusService } from '../../../../shared/services/application-coordination-status/application-coordination-status.service';
import { nameOfClass } from '../../../../../../core/functions/string-helpers';
import { CoordinationStatusResponse } from '../../../../coordination/coordination-results/models/coordination-status-response';
import { formatToDate } from '../../../../../../core/functions/date-helpers';

@Injectable({
    providedIn: 'root',
})
export class GoSearchInformationService {
    private response: CoordinationStatusResponse | null = null;

    private _remainingMs$: BehaviorSubject<number> = new BehaviorSubject(0);
    private _percentRemaining$: BehaviorSubject<number> = new BehaviorSubject(
        1,
    ); // 1 .. 0

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private timerId: any;
    private endAtMs = 0;

    public totalMs: number = 0;

    public readonly remainingMs$: Observable<number> =
        this._remainingMs$.asObservable();

    public readonly percentRemaining$: Observable<number> =
        this._percentRemaining$.asObservable();

    constructor(
        private readonly applicationCoordinationStatusService: ApplicationCoordinationStatusService,
    ) {
        this.initialize();
    }

    initialize(): void {
        this.response =
            this.applicationCoordinationStatusService.getCurrentApplicationCoordinationStatus();

        this.guardAgainstNullApplicationCoordinationStatusResponse();

        this.totalMs = this.calculateTotalMs();

        this._remainingMs$.next(this.totalMs);

        this.setupTimingAndStart();
    }

    onResultsExpired(expiredCallBack: () => void): Observable<number> {
        return this.remainingMs$.pipe(
            tap((value: number) => {
                if (value <= 0) {
                    expiredCallBack();
                }
            }),
        );
    }

    getApplicationCoordinationStatus(): CoordinationStatusResponse {
        return this.response!;
    }

    getFormattedTimeRemaining(remainingMs: number): string {
        const totalMinutes = Math.ceil(remainingMs / 60000); // round up
        const h = Math.floor(totalMinutes / 60);
        let m = totalMinutes % 60;

        if (m !== 0) {
            m -= 1;
        }

        if (h > 0) {
            return `${h}h ${m}m`;
        }

        return `${m}m`;
    }

    private calculateTotalMs(): number {
        this.guardAgainstNullApplicationCoordinationStatusResponse();

        if (
            !this.response?.coordinationCompletedAt &&
            !this.response?.coordinationResultsExpireAt
        ) {
            return 0;
        }

        if (
            !this.response?.coordinationCompletedAt ||
            !this.response?.coordinationResultsExpireAt
        ) {
            throw new Error(
                'Cannot setup coordination search timing, application coordination status response has null start/end dates.',
            );
        }

        const endTime = formatToDate(
            this.response?.coordinationResultsExpireAt,
        );
        const startTime = formatToDate(this.response?.coordinationCompletedAt);

        if (!endTime || !startTime) {
            throw new Error(
                `Cannot calculate totalMs in ${nameOfClass(GoSearchInformationService)}: invalid start/end dates.`,
            );
        }

        return endTime.getTime() - startTime.getTime();
    }

    private guardAgainstNullApplicationCoordinationStatusResponse(): void {
        if (this.response === null) {
            throw new Error(
                `Cannot initialize ${nameOfClass(GoSearchInformationService)}: application coordination status response is null. A resolver should be used to retrieve the status.`,
            );
        }
    }

    private setupTimingAndStart(): void {
        clearInterval(this.timerId);

        if (
            !this.response?.coordinationCompletedAt &&
            !this.response?.coordinationResultsExpireAt
        ) {
            this.tick();

            return;
        }

        if (
            !this.response?.coordinationCompletedAt ||
            !this.response?.coordinationResultsExpireAt
        ) {
            throw new Error(
                'Cannot setup coordination search timing, application coordination status response has null start/end dates.',
            );
        }

        const parsedStart = formatToDate(
            this.response?.coordinationCompletedAt,
        );
        const parsedEnd = formatToDate(
            this.response?.coordinationResultsExpireAt,
        );

        if (
            parsedStart &&
            parsedEnd &&
            parsedEnd.getTime() > parsedStart.getTime()
        ) {
            const startMs = parsedStart.getTime();
            this.endAtMs = parsedEnd.getTime();
            this._remainingMs$.next(this.endAtMs - startMs);
        } else {
            throw new Error(
                `Cannot intialize ${nameOfClass(GoSearchInformationService)}: invalid start/end dates.`,
            );
        }

        this.tick();

        this.timerId = setInterval(() => this.tick(), 1000);
    }

    private tick() {
        const now = Date.now();
        const remaining = Math.max(0, this.endAtMs - now);

        this._remainingMs$.next(remaining);

        // Protect against divide-by-zero if inputs equal or invalid.
        const denom = this.totalMs > 0 ? this.totalMs : 1;

        this._percentRemaining$.next(
            Math.max(0, Math.min(1, remaining / denom)),
        );

        if (remaining <= 0) {
            clearInterval(this.timerId);
        }
    }
}
